import { useState, useRef, useEffect } from "react";
import SingleDatePicker from "../components/SingleDatePicker";

const ContextMenu = ({ x, y, wo, onAction, onClose, noPermission = false }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [position, setPosition] = useState({ top: y, left: x });
  const menuRef = useRef(null);

  // Handle clicks outside the menu to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Adjust menu position after render
  useEffect(() => {
    const adjustPosition = () => {
      const menu = menuRef.current;
      if (!menu) return;

      const menuRect = menu.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const scrollX = window.scrollX || window.pageXOffset;
      const scrollY = window.scrollY || window.pageYOffset;

      let adjustedX = x;
      let adjustedY = y;

      const viewportX = x - scrollX;
      const viewportY = y - scrollY;

      if (viewportX + menuRect.width > viewportWidth - 10) {
        adjustedX = scrollX + viewportWidth - menuRect.width - 10;
      }
      if (viewportY + menuRect.height > viewportHeight - 10) {
        adjustedY = scrollY + viewportHeight - menuRect.height - 10;
      }
      adjustedX = Math.max(scrollX + 10, adjustedX);
      adjustedY = Math.max(scrollY + 10, adjustedY);

      setPosition({ top: adjustedY, left: adjustedX });
    };

    adjustPosition();
    window.addEventListener("resize", adjustPosition);
    window.addEventListener("scroll", adjustPosition);
    return () => {
      window.removeEventListener("resize", adjustPosition);
      window.removeEventListener("scroll", adjustPosition);
    };
  }, [x, y]);

  const handleAction = async (action, data) => {
    await onAction(action, data);
    onClose();
  };

  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  return (
    <div
      ref={menuRef}
      className="absolute bg-white border border-gray-200 rounded-lg shadow-xl z-50"
      style={{ top: position.top, left: position.left }}
      role="menu"
      aria-label={`Context menu for Work Order ${wo.woNumber}`}
    >
      {noPermission ? (
        // No Permission View
        <div className="p-4 min-w-64 max-w-80">
          <div className="flex items-start gap-3">
            <svg
              className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <h4 className="text-sm font-semibold text-gray-900">
                No Permission
              </h4>
              <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                You do not have permission to modify this work order using the
                context menu.
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Contact your administrator if you need access.
              </p>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={onClose}
              className="text-xs text-gray-600 hover:text-gray-800 underline focus:outline-none focus:ring-2 focus:ring-gray-400 rounded"
            >
              Close
            </button>
          </div>
        </div>
      ) : (
        // Normal Menu View
        <div className="flex flex-col gap-1 p-2 min-w-56">
          <div className="font-semibold text-gray-900 px-2 py-1 text-sm">
            Initial / Final Quantity
          </div>
          <div className="px-2 py-1 text-xs text-gray-600">
            Final ERPAG: {wo.currentErpagQty ?? wo.plannedQty ?? 0}
            <br />
            Initial: {wo.kpiPlannedQty ?? wo.plannedQty ?? 0}
          </div>
          <button
            onClick={() => handleAction("mirrorKpiPlannedQty", "plannedQty")}
            disabled={wo.kpiQtyLocked}
            className="px-2 py-1 text-sm text-gray-900 hover:bg-gray-100 rounded transition-all duration-200 focus:ring-2 focus:ring-gray-500 focus:outline-none text-left disabled:text-gray-400 disabled:hover:bg-white disabled:cursor-not-allowed"
            role="menuitem"
          >
            Mirror Initial Qty
          </button>
          <button
            onClick={() =>
              handleAction("mirrorKpiPlannedQty", "currentErpagQty")
            }
            disabled={wo.kpiQtyLocked}
            className="px-2 py-1 text-sm text-gray-900 hover:bg-gray-100 rounded transition-all duration-200 focus:ring-2 focus:ring-gray-500 focus:outline-none text-left disabled:text-gray-400 disabled:hover:bg-white disabled:cursor-not-allowed"
            role="menuitem"
          >
            Mirror Final ERPAG Qty
          </button>
          {wo.kpiQtyLocked ? (
            <button
              onClick={() => handleAction("unlockKpiQty")}
              className="px-2 py-1 text-sm text-gray-900 hover:bg-gray-100 rounded transition-all duration-200 focus:ring-2 focus:ring-gray-500 focus:outline-none text-left"
              role="menuitem"
            >
              Unlock Initial Qty
            </button>
          ) : (
            <button
              onClick={() => handleAction("lockKpiQty")}
              className="px-2 py-1 text-sm text-gray-900 hover:bg-gray-100 rounded transition-all duration-200 focus:ring-2 focus:ring-gray-500 focus:outline-none text-left"
              role="menuitem"
            >
              Lock Initial Qty
            </button>
          )}

          <div className="border-t border-gray-300 my-1"></div>
          <button
            onClick={() => handleAction("splitWorkOrder")}
            className="px-2 py-1 text-sm text-gray-900 hover:bg-gray-100 rounded transition-all duration-200 focus:ring-2 focus:ring-gray-500 focus:outline-none text-left w-full"
            role="menuitem"
          >
            Split Work Order
          </button>

          <div className="px-2 py-1">
            <label
              htmlFor="moveToDate"
              className="block text-sm font-semibold text-gray-900 mb-1"
            >
              Move to Specific Date
            </label>
            <SingleDatePicker
              id="moveToDate"
              value={selectedDate}
              onChange={(date) => {
                setSelectedDate(date);
                handleAction("moveToDate", formatLocalDate(date));
              }}
              minDate={new Date()}
              placeholderText="Select date"
              className="text-gray-900 w-full py-1 px-2 rounded border border-gray-300 text-sm focus:ring-2 focus:ring-gray-500 focus:outline-none"
              aria-label="Select date to move work order"
            />
          </div>
          <div className="border-t border-gray-300 my-1"></div>

          
        </div>
      )}
    </div>
  );
};

export default ContextMenu;
